import {
    Component,
    ElementRef,
    HostListener,
    OnDestroy,
    QueryList,
    ViewChildren,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
    Subject,
    concatMap,
    debounceTime,
    delay,
    distinctUntilChanged,
    map,
    of,
    takeUntil,
} from 'rxjs';
import { PATHS } from 'src-ironside/app-routing.module';
import { Filter } from 'src-ironside/repositories/common.repository';
import {
    DynamicProductParam,
    ProductRepository,
} from 'src-ironside/repositories/product.repository';
import { ResourceRepository } from 'src-ironside/repositories/resource.repository';
import { Supplier, SupplierRepository } from 'src-ironside/repositories/supplier.repository';
import { AccueilDataService } from 'src-ironside/services/accueil-data.service';
import { PermissionService } from 'src-ironside/services/permission.service';
import { ScreenMediator } from 'src-ironside/services/screen.mediator';
import { SessionStorageService } from 'src-ironside/services/session-storage.service';
import { BreadCrumbItem } from 'src-ironside/shared/components/bread-crumb/bread-crumb.component';
import { LazyListCompontent } from 'src-ironside/shared/components/lazy-list-component';
import { DynamicViewInfo } from 'src/app/shared/entities/dynamic-view';
import { DynamicDocumentService } from 'src/app/shared/services/dynamic-document.service';

interface PageEvent {
    first: number;
    rows: number;
    page: number;
    pageCount: number;
}

@Component({
    selector: 'app-fournisseurs-ironside',
    templateUrl: 'fournisseurs-ironside.component.html',
    styleUrls: ['./fournisseurs-ironside.component.css'],
    providers: [SupplierRepository, ProductRepository, ResourceRepository],
})
export class FournisseursIronsideComponent extends LazyListCompontent implements OnDestroy {
    @HostListener('document:click', ['$event']) onDocumentClick(event: any): void {
        if (this.exporterStautus) {
            if (
                event.target.className !== 'btn-common' &&
                event.target.className !== 'exporter-item d-flex align-items-center'
            ) {
                this.exporterStautus = false;
            }
        }
    }

    @ViewChildren('lazyItem')
    set lazyItem(ql: QueryList<ElementRef>) {
        this.setLazyItems(ql);
    }

    destroyRef = new Subject();
    searchTerm$ = new Subject<string>();

    companyName: string = '';
    searchContent: string = '';

    listSuppliers: Supplier[];
    exportViews?: DynamicViewInfo[];
    autocompleteSearchlistSuppliers: any[] = [];
    productsResult: any = [];
    listRows: any[] = [10, 20, 30];
    totalRecords: number = 120;
    pageSize: number = 30;
    first: number = 0;

    exporterStautus: boolean = false;
    loading: boolean = false;
    loadingExport: boolean = false;

    constructor(
        private screenMediator: ScreenMediator,
        private supplierRepo: SupplierRepository,
        private accueilDataService: AccueilDataService,
        private dynamicDocumentService: DynamicDocumentService,
        private sessionStorageService: SessionStorageService,
        private translate: TranslateService,
        public permissionService: PermissionService
    ) {
        super();

        this.screenMediator.clearHistory();

        this.handleLoadLazyData();
    }
    ngOnInit() {
        this.setSearchPipe();
        this.getListSuppliers();
    }

    get breadCrumb() {
        let breadCrumbs: BreadCrumbItem[] = [
            { name: this.translate.instant('Menu.home'), url: PATHS.home_en },
            { name: this.translate.instant('Menu.suppliers') },
        ];
        return breadCrumbs;
    }

    onPageChange3(event: PageEvent) {
        this.first = event.first;
        this.getListSuppliers(event.page + 1);
    }

    getListSuppliers(page: number = 1) {
        const filter: Filter[] = [...this.supplierRepo.defaultFilters];

        if (this.companyName) {
            filter.push({
                fieldName: 'companyName',
                conditions: [
                    {
                        type: 'CONTAINS',
                        value: this.companyName,
                    },
                ],
            });
        }

        const body: DynamicProductParam = {
            collectionName: 'dynamic_legal_entity',
            filters: filter,
            page: page,
            pageSize: this.pageSize,
            fields: ['logo', 'companyName', 'phone', 'fax', 'email', 'city', 'code'],
            sorts: { companyName: 'ASC' },
        };
        this.supplierRepo.find(page, this.pageSize, 'ASC', this.companyName);
        this.dynamicDocumentService
            .getListDynamicDocumentByCollectionName(body)
            .pipe(
                map((res) => {
                    return {
                        content: res.content.map((supplier: any) => ({
                            id: supplier.id,
                            title: supplier.companyName,
                            phone: supplier.phone,
                            fax: supplier.fax,
                            email: supplier.email,
                            city: supplier.city,
                            code: supplier.code,
                            resources: supplier.linkToResources?.map(
                                (resource: any) => resource.documentId
                            ),
                            logo: supplier?.logo?.[0]?.url,
                        })),
                        totalElements: res.totalElements,
                    };
                })
            )
            .pipe(takeUntil(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.listSuppliers = res.content;
                    this.totalRecords = res.totalElements;
                },
                error: (error) => {},
            });
        window.scrollTo(0, 0);
    }

    goCatalogue(supplier: Supplier | undefined) {
        if (!supplier?.code || !supplier.title || !supplier.id) {
            return;
        }
        if (supplier.id) {
            this.accueilDataService.filterData = {};
            this.screenMediator.toFournisseurDetail(supplier.id, supplier.title, 'detail');
        }

        this.sessionStorageService.setFilters({ suppliers: [supplier.id] });
    }

    onSearch() {
        this.accueilDataService.filterData = { research: this.searchContent };
        this.screenMediator.toRecherche();
    }

    onKeyUp(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            this.getListSuppliers();
        }
    }

    setSearchPipe() {
        this.searchTerm$
            .pipe(debounceTime(300), distinctUntilChanged())
            .pipe(takeUntil(this.destroyRef))
            .subscribe((searchTerm: string) => {
                if (searchTerm) {
                    this.autocomplete(searchTerm);
                } else {
                    this.productsResult = [];
                }
            });
    }

    autocomplete(term: string) {
        if (term.length >= 3) {
            const filter: Filter[] = [...this.supplierRepo.defaultFilters];

            filter.push({
                fieldName: 'companyName',
                conditions: [
                    {
                        type: 'CONTAINS',
                        value: this.companyName,
                    },
                ],
            });

            const body: DynamicProductParam = {
                collectionName: 'dynamic_legal_entity',
                filters: filter,
                page: 1,
                pageSize: this.pageSize,
                fields: ['companyName', 'code'],
                sorts: { companyName: 'ASC' },
            };
            this.loading = true;
            this.dynamicDocumentService
                .getListDynamicDocumentByCollectionName(body)
                .pipe(
                    map((res) => {
                        return {
                            content: res.content.map((supplier: any) => ({
                                id: supplier.id,
                                title: supplier.companyName,
                                phone: supplier.phone,
                                fax: supplier.fax,
                                email: supplier.email,
                                city: supplier.city,
                                code: supplier.code,
                                resources: supplier.linkToResources?.map(
                                    (resource: any) => resource.documentId
                                ),
                                logo: supplier?.logo?.[0]?.url,
                            })),
                            totalElements: res.totalElements,
                        };
                    })
                )
                .pipe(takeUntil(this.destroyRef))
                .subscribe({
                    next: (res) => {
                        this.autocompleteSearchlistSuppliers = res?.content;
                        this.loading = false;
                    },
                    error: (error) => {
                        this.loading = false;
                    },
                });
        }
    }

    emptyList() {
        setTimeout(() => {
            this.autocompleteSearchlistSuppliers = [];
        }, 300);
    }

    handleLoadLazyData() {
        this.onNeedToLoad$
            .pipe(
                takeUntil(this.destroyRef),
                concatMap((x) => of(x).pipe(delay(200))) //delay load API 0.2s
            )
            .subscribe((id) => {
                const supplier = this.listSuppliers?.find((supplier) => supplier.id === id);

                if (!supplier) return;
            });
    }

    dropdownExporter(): void {
        if (!this.exportViews) {
            this.supplierRepo
                .getViews()
                .pipe(takeUntil(this.destroyRef))
                .subscribe((res) => {
                    this.exportViews = res;
                });
        }
        this.exporterStautus = !this.exporterStautus;
    }

    export(viewId: string) {
        this.supplierRepo.export(viewId);
    }

    onExporterFournisseur() {
        this.loadingExport = true;
        const exportFileRequest = {
            filters: [
                {
                    fieldName: 'typeOfLegalEntity',
                    conditions: [
                        {
                            type: 'EQUALS',
                            value: 'FOURNISSEUR',
                        },
                    ],
                },
            ],
            locales: ['fr_FR'],
            sort: {},
            fields: [
                'companyName',
                'address',
                'postcode',
                'city',
                'phone',
                'email',
                'linkToNaturalPersons',
            ],
            relationFields: {
                linkToNaturalPersons: [
                    'civility',
                    'lastName',
                    'firstName',
                    'job',
                    'phone',
                    'mobile',
                    'email',
                ],
            },
        };
        this.dynamicDocumentService
            .exportFileSuplier('dynamic_legal_entity', 'xlsx', exportFileRequest)
            .pipe(takeUntil(this.destroyRef))
            .subscribe({
                next: (data: Blob) => {
                    const nameExport = 'export_fournisseur.xlsx';
                    const blob = new Blob([data], { type: 'application/vnd.ms-excel' });
                    const objectUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = objectUrl;
                    link.download = nameExport;
                    link.click();
                    URL.revokeObjectURL(objectUrl);
                    this.loadingExport = false;
                },
                error: (err) => {
                    this.loadingExport = false;
                },
            });
    }

    selectItem(item: any) {
        if (item.id) {
            this.accueilDataService.filterData = {};
            this.screenMediator.toFournisseurDetail(item.id, item.title, 'detail');
            this.sessionStorageService.setFilters({ suppliers: [item.id] });
        }
    }

    onSearchInput(): void {
        this.searchTerm$.next(this.companyName);
    }

    goSupplierDetail(supplier: Supplier) {
        if (!supplier?.code || !supplier.title || !supplier.id) {
            return;
        }
        if (supplier.id) {
            this.accueilDataService.filterData = {};
            this.screenMediator.toFournisseurDetail(supplier.id, supplier.title, 'detail-supplier');
        }
    }

    ngOnDestroy(): void {
        this.destroyRef.next(null);
        this.destroyRef.complete();
    }
}
